import { useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { SearchesList } from './pages/SearchesList';
import { AddSearch } from './pages/AddSearch';
import EditSearch from './pages/EditSearch';
import { SubscriptionPage } from './pages/Subscription';
import { InfoMenu } from './pages/InfoMenu';
import { HowToFindLink } from './pages/HowToFindLink';
import { Contacts } from './pages/Contacts';
import { Legal } from './pages/Legal';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminUserSearches from './pages/AdminUserSearches';
import AdminActiveSearches from './pages/AdminActiveSearches';
import AdminProxy from './pages/AdminProxy';
import AdminGrowth from './pages/AdminGrowth';
import AdminSpeed from './pages/AdminSpeed';
import AdminBroadcasts from './pages/AdminBroadcasts';
import AdminBroadcastDetail from './pages/AdminBroadcastDetail';
import AdminPayments from './pages/AdminPayments';
import AdminAlerts from './pages/AdminAlerts';
import './index.css';

function TelegramAuth({ onReady }: { onReady: () => void }) {
  const { login, isAuthenticated } = useAuth();
  const [authAttempted, setAuthAttempted] = useState(false);

  useEffect(() => {
    const tg = (window as any).Telegram?.WebApp;
    if (!tg) {
      onReady();
      return;
    }

    tg.ready();
    tg.expand();

    const theme = tg.themeParams;
    if (theme) {
      document.documentElement.style.setProperty('--tg-theme-bg-color', theme.bg_color || '#ffffff');
      document.documentElement.style.setProperty('--tg-theme-text-color', theme.text_color || '#000000');
      document.documentElement.style.setProperty('--tg-theme-hint-color', theme.hint_color || '#999999');
      document.documentElement.style.setProperty('--tg-theme-link-color', theme.link_color || '#2481cc');
      document.documentElement.style.setProperty('--tg-theme-button-color', theme.button_color || '#2481cc');
      document.documentElement.style.setProperty('--tg-theme-button-text-color', theme.button_text_color || '#ffffff');
      document.documentElement.style.setProperty('--tg-theme-secondary-bg-color', theme.secondary_bg_color || '#f5f5f5');
    }

    const initData = tg.initData;
    if (initData && !isAuthenticated && !authAttempted) {
      setAuthAttempted(true);
      login(initData)
        .catch((err: any) => {
          console.error('Auth failed:', err);
        })
        .finally(() => onReady());
    } else {
      onReady();
    }
  }, [login, isAuthenticated, authAttempted, onReady]);

  return null;
}

function AdminRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!user?.is_admin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const [authReady, setAuthReady] = useState(false);

  if (!authReady) {
    return (
      <>
        <TelegramAuth onReady={() => setAuthReady(true)} />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <div style={{ width: '32px', height: '32px', border: '3px solid #e5e7eb', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      </>
    );
  }

  return (
    <>
      <Routes>
        {/* Layout is a layout route: it renders the tab bar plus an <Outlet />
            for whichever child route below is matched. Previously Layout was
            used as a plain wrapper (<Layout><Routes>...</Routes></Layout>),
            so its <Outlet /> had no route context and rendered nothing —
            only the tab bar showed and no page content ever appeared. */}
        <Route element={<Layout />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/searches" element={<SearchesList />} />
          <Route path="/add" element={<AddSearch />} />
          <Route path="/searches/:id/edit" element={<EditSearch />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/info" element={<InfoMenu />} />
          <Route path="/info/how-to-find-link" element={<HowToFindLink />} />
          <Route path="/info/contacts" element={<Contacts />} />
          <Route path="/info/legal" element={<Legal />} />
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
          <Route path="/admin/users/:userId/searches" element={<AdminRoute><AdminUserSearches /></AdminRoute>} />
          <Route path="/admin/active-searches" element={<AdminRoute><AdminActiveSearches /></AdminRoute>} />
          <Route path="/admin/proxy" element={<AdminRoute><AdminProxy /></AdminRoute>} />
          <Route path="/admin/growth" element={<AdminRoute><AdminGrowth /></AdminRoute>} />
          <Route path="/admin/speed" element={<AdminRoute><AdminSpeed /></AdminRoute>} />
          <Route path="/admin/broadcasts" element={<AdminRoute><AdminBroadcasts /></AdminRoute>} />
          <Route path="/admin/broadcasts/:id" element={<AdminRoute><AdminBroadcastDetail /></AdminRoute>} />
          <Route path="/admin/payments" element={<AdminRoute><AdminPayments /></AdminRoute>} />
          <Route path="/admin/alerts" element={<AdminRoute><AdminAlerts /></AdminRoute>} />
        </Route>
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
